import { useSeoTags } from "@/lib/seo";

/**
 * VOIHU Terms of Service — approved verbatim text (V2026-09).
 * Wording is NOT to be edited. Any change requires a fresh approval cycle
 * and a `terms_version` bump on the acceptance surfaces. Layout / spacing /
 * typography may be tuned; substantive text may not.
 */
export default function TermsPage() {
  useSeoTags({
    title: "Terms of Service — VOIHU",
    description:
      "Rules governing your use of VOIHU: MESSAGE creation, VOICE participation, " +
      "sharing, the €1 participation fee, moderation and consumer rights. " +
      "Controller: CA HOLDING S.R.L.S. (Italy). Contact: support@voihuworld.com.",
    canonical: "https://voihuworld.com/terms",
  });

  return (
    <main className="bg-[color:var(--oh-bg)] text-[color:var(--oh-ink)]"
          data-testid="terms-page">
      <article className="max-w-[720px] mx-auto px-6 sm:px-10 pt-16 pb-24">
        <div className="oh-label" data-testid="terms-eyebrow">Legal</div>

        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4"
            data-testid="terms-h1">
          VOIHU Terms of Service
        </h1>

        <p className="oh-label mt-6" data-testid="terms-updated">Last updated: September 2026</p>

        <div className="mt-10 space-y-6 text-base sm:text-[17px] leading-[1.75]"
             data-testid="terms-body">
          <p>Welcome to VOIHU.</p>
          <p>
            VOIHU is built around a simple idea: one human voice can travel from
            person to person and, together with others, become difficult to
            ignore.
          </p>
          <p>
            These Terms of Service (“Terms”) govern your access to and use of
            VOIHU, including the creation of MESSAGEs, the addition of VOICES,
            sharing and propagation features, payments, impact visualisations
            and related services.
          </p>
          <p>
            By creating an account or using features of VOIHU that require
            acceptance of these Terms, you agree to be bound by them.
          </p>
          <p>If you do not agree, you should not use those features.</p>

          <Section n="1" title="Who operates VOIHU">
            <p>VOIHU is operated by:</p>
            <Address>
              <div>CA HOLDING S.R.L.S.</div>
              <div>Viale Italia 8/C</div>
              <div>25126 Brescia (BS)</div>
              <div>Italy</div>
              <div>Italian Tax Code / VAT No.: 04672180983</div>
              <div>REA: BS-632332</div>
              <div>Certified Email (PEC): caholding@pec.it</div>
              <div>Service / trading name: VOIHU</div>
              <div>Website: voihuworld.com</div>
              <div>Support: support@voihuworld.com</div>
            </Address>
            <p>
              In these Terms, “VOIHU”, “we”, “us” and “our” refer to CA HOLDING
              S.R.L.S. in connection with the VOIHU service.
            </p>
          </Section>

          <Section n="2" title="What VOIHU does">
            <p>
              VOIHU allows people to create MESSAGEs and allows other people to
              add their VOICE and pass those MESSAGEs onward.
            </p>
            <p>
              A MESSAGE may therefore travel through a chain of people, cities
              and countries.
            </p>
            <p>
              VOIHU may provide features showing that journey, including Global
              Impact and My Impact.
            </p>
            <p>
              VOIHU does not guarantee that any MESSAGE will receive a particular
              number of VOICES, reach a particular person or organisation,
              travel to a particular country, become viral, receive a response,
              or achieve the objective sought by its creator.
            </p>
            <p>
              VOIHU provides the means for a MESSAGE to travel. It does not
              guarantee where that journey will lead.
            </p>
          </Section>

          <Section n="3" title="Eligibility and age">
            <p>
              You must be at least 16 years old to create a VOIHU account,
              create a MESSAGE or add your VOICE to a MESSAGE.
            </p>
            <p>
              Where the law applicable to you requires a higher minimum age or
              additional parental or guardian authorisation, that requirement
              applies.
            </p>
            <p>
              By using a paid feature, you also confirm that you are legally
              entitled to use the payment method you provide or that you have
              the necessary authorisation from the person legally entitled to
              use it.
            </p>
            <p>
              Possession or use of a payment card does not itself establish
              that a user satisfies VOIHU’s minimum-age requirements.
            </p>
            <p>
              You may not use VOIHU if applicable law prohibits you from
              receiving or using the service.
            </p>
          </Section>

          <Section n="4" title="Your account">
            <p>You must provide accurate information where VOIHU requires it.</p>
            <p>
              You are responsible for activity carried out through your
              authenticated account and should take reasonable steps to protect
              access to your email account, authentication method and devices.
            </p>
            <p>You may not:</p>
            <List>
              <li>impersonate another person;</li>
              <li>create or use an account in order to deceive other users;</li>
              <li>obtain unauthorised access to another person’s account;</li>
              <li>
                sell or transfer an account where this would undermine the
                integrity of VOIHU;
              </li>
              <li>circumvent authentication or security mechanisms.</li>
            </List>
            <p>
              VOIHU may require additional verification where reasonably
              necessary to protect an account or the integrity of the platform.
            </p>
          </Section>

          <Section n="5" title="MESSAGEs">
            <p>
              A MESSAGE is content submitted by a user with the intention that
              it may be seen, supported and passed on by other people.
            </p>
            <p>Creating a MESSAGE does not create a right to publication.</p>
            <p>Before publication, VOIHU may apply:</p>
            <List>
              <li>semantic duplicate detection;</li>
              <li>content moderation;</li>
              <li>safety checks;</li>
              <li>technical checks;</li>
              <li>language detection;</li>
              <li>categorisation;</li>
              <li>other rules reasonably necessary to operate the platform.</li>
            </List>
            <p>
              A proposed MESSAGE may therefore be accepted, held for review,
              redirected to an existing substantially similar MESSAGE, or
              blocked.
            </p>
            <p>
              Where VOIHU provides an appeal or review mechanism, users may use
              that mechanism to challenge an affected decision.
            </p>
          </Section>

          <Section n="6" title="One meaning, one MESSAGE">
            <p>
              A core principle of VOIHU is that substantially the same MESSAGE
              should not be unnecessarily fragmented into multiple competing
              MESSAGEs.
            </p>
            <p>
              VOIHU may therefore use semantic analysis to determine whether a
              proposed MESSAGE substantially overlaps with an existing public
              MESSAGE.
            </p>
            <p>
              Where a substantial match is identified, VOIHU may direct the
              user to the existing MESSAGE instead of creating a new one.
            </p>
            <p>This process may use automated systems.</p>
            <p>
              Where available, a user who believes that the proposed MESSAGE is
              meaningfully different may request human review.
            </p>
            <p>
              VOIHU does not guarantee that semantic analysis will always be
              correct.
            </p>
          </Section>

          <Section n="7" title="Adding your VOICE">
            <p>Adding your VOICE means choosing to participate in a MESSAGE.</p>
            <p>Your VOICE may contribute to:</p>
            <List>
              <li>the MESSAGE’s public VOICE count;</li>
              <li>its propagation journey;</li>
              <li>country and location statistics;</li>
              <li>Global Impact;</li>
              <li>My Impact or another participant’s branch;</li>
              <li>other aggregate indicators relating to the MESSAGE.</li>
            </List>
            <p>
              Adding your VOICE does not mean that VOIHU independently verifies
              or endorses every factual statement contained in the MESSAGE.
            </p>
            <p>
              It represents the user’s decision to participate in that MESSAGE.
            </p>
            <p>
              A VOICE must not be created through automation, bots, fabricated
              identities or other mechanisms intended to artificially inflate
              participation.
            </p>
          </Section>

          <Section n="8" title="PASS IT ON and propagation">
            <p>VOIHU allows users to pass MESSAGEs to other people.</p>
            <p>
              Shared links may contain referral information allowing VOIHU to
              understand how a MESSAGE travelled.
            </p>
            <p>
              If another person follows your referral and subsequently
              participates, that activity may form part of your My Impact branch
              and the MESSAGE’s overall propagation journey.
            </p>
            <p>
              Propagation attribution is generated from VOIHU’s technical
              records and may not perfectly represent every real-world
              conversation or interaction that occurred outside the platform.
            </p>
            <p>
              You may not manipulate referral chains, create artificial
              propagation, use automated accounts or otherwise falsify the
              apparent journey of a MESSAGE.
            </p>
          </Section>

          <Section n="9" title="Geographic information">
            <p>
              Users select the city and country associated with their MESSAGE
              or VOICE.
            </p>
            <p>
              VOIHU may represent that location using approximate city-centre
              coordinates.
            </p>
            <p>
              You must not intentionally select a false location for the purpose
              of misleading other users or manipulating MESSAGE propagation.
            </p>
            <p>
              VOIHU does not represent city-level information as proof of a
              person’s precise physical location.
            </p>
            <p>
              Geographic visualisations are designed to illustrate the journey
              of a MESSAGE, not to provide precise tracking of individuals.
            </p>
          </Section>

          <Section n="10" title="Fees and payments">
            <p>Certain VOIHU activities may require payment.</p>
            <p>
              Unless expressly stated otherwise at the time of the transaction,
              the current participation fee for adding a paid VOICE is:
            </p>
            <p className="font-serif-editorial text-3xl mt-2">€1</p>
            <p>
              Certain MESSAGEs or campaigns may be offered free of charge. This
              includes, where designated by VOIHU, a founding or promotional
              MESSAGE.
            </p>
            <p>
              The applicable price is the price clearly displayed before the
              user confirms the transaction.
            </p>
            <p>Payments are processed through Stripe.</p>
            <p>VOIHU does not store full payment-card details.</p>
            <p>
              You must use only a payment method that you are legally
              authorised to use.
            </p>
            <p>
              VOIHU may change prices for future transactions. A price change
              does not retroactively change a transaction already completed.
            </p>
          </Section>

          <Section n="11" title="Immediate performance and consumer rights">
            <p>
              VOIHU features associated with a payment are generally intended
              to be performed immediately after successful payment.
            </p>
            <p>
              Where applicable consumer law grants a right of withdrawal,
              cancellation, refund, price reduction, termination or another
              mandatory remedy, those rights remain unaffected by these Terms.
            </p>
            <p>
              Where applicable law permits immediate performance of a digital
              service or digital content during a statutory withdrawal period,
              VOIHU may request the user’s express consent or request for
              immediate performance and, where legally required, acknowledgement
              of the consequences for the right of withdrawal.
            </p>
            <p>
              Nothing in these Terms excludes or restricts consumer rights that
              cannot lawfully be excluded or restricted.
            </p>
          </Section>

          <Section n="12" title="Refunds">
            <p>
              A completed payment is not automatically refundable merely
              because:
            </p>
            <List>
              <li>a MESSAGE does not become popular;</li>
              <li>
                a MESSAGE does not reach a particular person or organisation;
              </li>
              <li>a user’s branch does not generate additional VOICES;</li>
              <li>
                the user later changes their mind about supporting a MESSAGE;
              </li>
              <li>another user does not pass the MESSAGE onward.</li>
            </List>
            <p>However, refunds will be provided where required by applicable law.</p>
            <p>
              VOIHU may also issue refunds in other circumstances where
              appropriate, including duplicate or erroneous transactions,
              technical failures, or other cases that VOIHU reasonably
              determines justify a refund.
            </p>
            <p>To raise a payment issue, contact:</p>
            <p>
              <a href="mailto:support@voihuworld.com"
                 className="underline underline-offset-4">support@voihuworld.com</a>
            </p>
            <p>Nothing in this section limits mandatory consumer remedies.</p>
          </Section>

          <Section n="13" title="Content you provide">
            <p>
              You retain ownership of content you create and submit to VOIHU,
              subject to any rights belonging to other people.
            </p>
            <p>
              You are responsible for ensuring that you have the rights
              necessary to submit that content.
            </p>
            <p>
              By submitting a MESSAGE, photograph or other content intended for
              publication, you grant VOIHU a non-exclusive, worldwide,
              royalty-free licence to host, store, reproduce, display, format,
              adapt for technical presentation, translate, distribute and make
              that content available as reasonably necessary to:
            </p>
            <List>
              <li>operate VOIHU;</li>
              <li>display the MESSAGE;</li>
              <li>enable its propagation;</li>
              <li>generate translations;</li>
              <li>display sharing previews;</li>
              <li>provide impact visualisations;</li>
              <li>moderate and protect the platform;</li>
              <li>allow other users to view and share the MESSAGE.</li>
            </List>
            <p>This licence does not transfer ownership of your content to VOIHU.</p>
            <p>
              The licence lasts for as long as reasonably necessary to operate
              the MESSAGE and the service, subject to applicable law and valid
              deletion or other rights requests.
            </p>
            <p>
              VOIHU will not acquire ownership of your MESSAGE merely because
              it travels through the platform.
            </p>
          </Section>

          <Section n="14" title="Photographs and third-party rights">
            <p>
              If you upload a photograph or other media, you confirm that you
              have the right to use and publish it through VOIHU.
            </p>
            <p>You must not upload content that unlawfully infringes:</p>
            <List>
              <li>copyright;</li>
              <li>trademarks;</li>
              <li>privacy rights;</li>
              <li>image or personality rights;</li>
              <li>confidentiality;</li>
              <li>other rights belonging to third parties.</li>
            </List>
            <p>
              Where content contains identifiable people, you are responsible
              for ensuring that your use of that content is lawful.
            </p>
            <p>
              VOIHU may remove or restrict content where reasonably necessary in
              response to a valid rights complaint or legal obligation.
            </p>
          </Section>

          <Section n="15" title="Prohibited content and conduct">
            <p>
              You may not use VOIHU to create, promote, distribute or facilitate
              content or activity that is unlawful or that violates VOIHU’s
              safety rules.
            </p>
            <p>This includes, where applicable:</p>
            <List>
              <li>credible threats or incitement to violence;</li>
              <li>unlawful hate or targeted harassment;</li>
              <li>
                terrorism or violent-extremist promotion or recruitment;
              </li>
              <li>sexual exploitation or illegal sexual content;</li>
              <li>
                doxxing or unlawful disclosure of private personal information;
              </li>
              <li>fraud, scams or deceptive financial schemes;</li>
              <li>unlawful goods, services or activity;</li>
              <li>deliberate impersonation;</li>
              <li>
                malicious manipulation of VOIHU’s participation or propagation
                systems;
              </li>
              <li>automated or fabricated VOICES;</li>
              <li>
                attempts to circumvent moderation, payment or security systems;
              </li>
              <li>
                content that unlawfully infringes intellectual-property or
                privacy rights.
              </li>
            </List>
            <p>
              Context matters. Content discussing difficult or controversial
              subjects is not prohibited merely because the subject itself is
              sensitive.
            </p>
            <p>
              VOIHU is intended to allow human voices to be heard, including
              disagreement, criticism and debate, subject to applicable law and
              platform-safety requirements.
            </p>
          </Section>

          <Section n="16" title="Moderation and automated systems">
            <p>
              VOIHU uses automated systems, including artificial intelligence,
              to assist with moderation, semantic duplicate detection, language
              processing, categorisation and translation.
            </p>
            <p>Automated systems can make mistakes.</p>
            <p>
              A moderation decision may result in a proposed MESSAGE being
              classified as SAFE, REVIEW or BLOCKED.
            </p>
            <p>
              Semantic analysis may result in a proposed MESSAGE being directed
              to an existing MESSAGE.
            </p>
            <p>
              Where VOIHU provides an appeal or review mechanism, affected
              users may request human review.
            </p>
            <p>
              VOIHU may also take manual action where necessary to enforce
              these Terms, comply with law, respond to reports or protect users
              and the platform.
            </p>
          </Section>

          <Section n="17" title="HEARD">
            <p>
              VOIHU may provide features that allow a MESSAGE to be marked or
              claimed as having been heard, acknowledged or received by an
              intended person, organisation or representative.
            </p>
            <p>
              A HEARD status means only what the relevant VOIHU feature
              expressly states.
            </p>
            <p>It does not necessarily mean that:</p>
            <List>
              <li>the recipient agrees with the MESSAGE;</li>
              <li>the recipient supports it;</li>
              <li>the recipient promises to act;</li>
              <li>the recipient verified every statement within it;</li>
              <li>the MESSAGE achieved its objective.</li>
            </List>
            <p>
              Users must not falsify HEARD claims, impersonate intended
              recipients or manipulate verification mechanisms.
            </p>
            <p>
              VOIHU may preserve audit information reasonably necessary to
              protect the integrity of HEARD claims.
            </p>
          </Section>

          <Section n="18" title="Reports, appeals and reviews">
            <p>
              Users may be able to report MESSAGEs or request review of certain
              automated decisions.
            </p>
            <p>Reports and appeals must be made in good faith.</p>
            <p>
              You may not abuse these systems to harass other users, repeatedly
              submit knowingly false claims or attempt to manipulate moderation.
            </p>
            <p>
              VOIHU may retain relevant records where reasonably necessary to
              review the matter, prevent abuse, resolve disputes or comply with
              legal obligations.
            </p>
          </Section>

          <Section n="19" title="Intellectual property belonging to VOIHU">
            <p>
              The VOIHU service, excluding user-generated content, including
              its brand, logos, design, software, interface, original graphics,
              visual identity and proprietary platform elements, belongs to
              VOIHU or its licensors and is protected by applicable
              intellectual-property laws.
            </p>
            <p>
              These Terms do not grant you ownership of VOIHU’s intellectual
              property.
            </p>
            <p>
              You may not copy, reproduce, reverse engineer, exploit or use
              VOIHU’s protected elements except as permitted by law or with
              prior authorisation.
            </p>
            <p>
              Nothing in this section prevents ordinary use of VOIHU’s built-in
              sharing functionality.
            </p>
          </Section>

          <Section n="20" title="Platform integrity">
            <p>You may not:</p>
            <List>
              <li>interfere with the operation of VOIHU;</li>
              <li>probe or circumvent security controls without authorisation;</li>
              <li>
                scrape the service at a scale or in a manner that disrupts
                VOIHU or violates applicable law;
              </li>
              <li>
                use bots to create accounts, VOICES or artificial propagation;
              </li>
              <li>manipulate counters or impact statistics;</li>
              <li>attempt unauthorised access to data or systems;</li>
              <li>introduce malware or malicious code;</li>
              <li>
                use VOIHU in a manner designed to damage the service or other
                users.
              </li>
            </List>
            <p>
              VOIHU may take reasonable technical measures to detect and
              prevent such activity.
            </p>
          </Section>

          <Section n="21" title="Suspension, restriction and termination">
            <p>
              VOIHU may restrict, suspend or terminate access to all or part of
              the service where reasonably necessary because of:
            </p>
            <List>
              <li>serious or repeated violations of these Terms;</li>
              <li>unlawful activity;</li>
              <li>fraud or payment abuse;</li>
              <li>threats to platform or user security;</li>
              <li>manipulation of VOICES or propagation;</li>
              <li>legal or regulatory requirements;</li>
              <li>serious misuse of moderation or verification systems.</li>
            </List>
            <p>
              Where appropriate and legally required, VOIHU may provide notice
              or an opportunity to challenge the decision.
            </p>
            <p>
              VOIHU will not use this provision to remove mandatory consumer
              rights.
            </p>
            <p>You may stop using VOIHU at any time.</p>
            <p>
              Requests concerning personal data or account information can be
              sent to{" "}
              <a href="mailto:support@voihuworld.com"
                 className="underline underline-offset-4">support@voihuworld.com</a>.
            </p>
          </Section>

          <Section n="22" title="Availability and changes to VOIHU">
            <p>VOIHU is an evolving service.</p>
            <p>Features may be added, changed, improved or removed.</p>
            <p>
              VOIHU does not guarantee uninterrupted or error-free availability.
            </p>
            <p>
              Temporary interruptions may occur because of maintenance,
              security incidents, provider outages, technical problems or
              circumstances outside VOIHU’s reasonable control.
            </p>
            <p>
              Where a paid digital service is affected, any mandatory rights
              available to consumers under applicable law remain unaffected.
            </p>
            <p>
              VOIHU will not make changes to paid digital services in a manner
              that unlawfully deprives consumers of mandatory rights.
            </p>
          </Section>

          <Section n="23" title="Third-party services">
            <p>
              VOIHU may interact with third-party services, including payment
              providers, authentication providers, sharing platforms and other
              infrastructure providers.
            </p>
            <p>Third-party services operate under their own terms and policies.</p>
            <p>
              VOIHU is not responsible for third-party services to the extent
              that the relevant matter is outside VOIHU’s control, subject
              always to responsibilities that applicable law places directly on
              VOIHU.
            </p>
          </Section>

          <Section n="24" title="No endorsement of MESSAGEs">
            <p>MESSAGEs are created by users.</p>
            <p>
              Unless VOIHU expressly states otherwise, publication or
              propagation of a MESSAGE does not mean that CA HOLDING S.R.L.S.
              or VOIHU endorses, verifies or agrees with the MESSAGE.
            </p>
            <p>
              Similarly, moderation approval means that a MESSAGE passed the
              applicable platform process; it does not constitute verification
              that every factual assertion within it is true.
            </p>
            <p>
              Users should exercise their own judgment when deciding whether to
              add their VOICE.
            </p>
          </Section>

          <Section n="25" title="Disclaimers">
            <p>
              To the extent permitted by applicable law, VOIHU is provided on
              an “as available” basis.
            </p>
            <p>VOIHU cannot guarantee:</p>
            <List>
              <li>uninterrupted availability;</li>
              <li>
                that every technical function will always operate without
                error;
              </li>
              <li>
                that automated moderation or translation will always be
                accurate;
              </li>
              <li>
                that every location or propagation attribution will be perfect;
              </li>
              <li>that a MESSAGE will achieve any particular result;</li>
              <li>that third-party services will always remain available.</li>
            </List>
            <p>
              Nothing in these Terms excludes warranties, guarantees or other
              protections that applicable consumer law does not permit us to
              exclude.
            </p>
          </Section>

          <Section n="26" title="Limitation of liability">
            <p>
              Nothing in these Terms excludes or limits liability where
              exclusion or limitation is prohibited by law.
            </p>
            <p>
              In particular, nothing limits liability for fraud, wilful
              misconduct or other liability that cannot lawfully be limited.
            </p>
            <p>
              To the maximum extent permitted by applicable law, VOIHU is not
              liable for indirect or consequential losses arising solely from
              circumstances outside its reasonable control or from a user’s
              unlawful or unauthorised use of the platform.
            </p>
            <p>
              Any limitation in these Terms applies only to the extent
              permitted by the mandatory law applicable to the user.
            </p>
            <p>Non-waivable consumer rights remain unaffected.</p>
          </Section>

          <Section n="27" title="Indemnity for business or unlawful misuse">
            <p>
              If you use VOIHU in the course of a business or professional
              activity, or deliberately use the service unlawfully, you are
              responsible, to the extent permitted by law, for losses
              reasonably arising from your violation of these Terms or
              third-party rights.
            </p>
            <p>
              This section does not impose an unfair indemnity obligation on
              consumers for ordinary use of VOIHU.
            </p>
          </Section>

          <Section n="28" title="Consumer rights">
            <p>
              If you are a consumer, nothing in these Terms removes rights
              granted to you by mandatory consumer-protection law.
            </p>
            <p>
              Depending on where you live, you may have mandatory rights
              concerning:
            </p>
            <List>
              <li>pre-contract information;</li>
              <li>digital-service conformity;</li>
              <li>withdrawal or cancellation;</li>
              <li>refunds;</li>
              <li>remedies for defective or unavailable digital services;</li>
              <li>unfair contract terms;</li>
              <li>dispute resolution;</li>
              <li>jurisdiction.</li>
            </List>
            <p>
              Where these Terms conflict with a mandatory consumer right that
              cannot lawfully be waived, the mandatory rule prevails.
            </p>
          </Section>

          <Section n="29" title="Governing law and jurisdiction">
            <p>
              These Terms are governed by Italian law, without depriving
              consumers of mandatory protections granted by the law that
              applies to them and cannot contractually be excluded.
            </p>
            <p>
              For users acting in the course of a business or professional
              activity, disputes arising from these Terms shall, to the extent
              permitted by law, be subject to the exclusive jurisdiction of the
              courts of Brescia, Italy.
            </p>
            <p>
              For consumers, jurisdiction is determined by applicable mandatory
              consumer and private-international-law rules.
            </p>
          </Section>

          <Section n="30" title="Changes to these Terms">
            <p>
              VOIHU may update these Terms where reasonably necessary because
              of:
            </p>
            <List>
              <li>changes to the service;</li>
              <li>new features;</li>
              <li>legal or regulatory developments;</li>
              <li>security requirements;</li>
              <li>changes to payment or technical infrastructure.</li>
            </List>
            <p>
              Material changes will be communicated where required by
              applicable law.
            </p>
            <p>
              Changes will not retroactively remove rights already accrued
              under completed transactions.
            </p>
            <p>
              The date at the beginning of these Terms indicates the latest
              revision.
            </p>
          </Section>

          <Section n="31" title="Severability">
            <p>
              If a provision of these Terms is found invalid or unenforceable,
              the remaining provisions remain effective to the extent permitted
              by law.
            </p>
            <p>
              The invalid provision will be interpreted or limited only to the
              extent necessary to make it lawful and enforceable where
              applicable.
            </p>
          </Section>

          <Section n="32" title="Entire agreement">
            <p>
              These Terms, together with any additional terms expressly
              presented for a specific VOIHU feature or transaction, govern the
              contractual relationship concerning use of VOIHU.
            </p>
            <p>
              The Privacy Policy explains how personal data is processed and
              forms part of VOIHU’s transparency framework but does not convert
              processing that relies on another lawful basis into consent
              merely by being referenced in these Terms.
            </p>
          </Section>

          <Section n="33" title="Contact">
            <p>
              Questions concerning these Terms or the VOIHU service can be sent
              to:
            </p>
            <Address>
              <div>CA HOLDING S.R.L.S.</div>
              <div>Viale Italia 8/C</div>
              <div>25126 Brescia (BS)</div>
              <div>Italy</div>
              <div>C.F./VAT No.: 04672180983</div>
              <div>REA: BS-632332</div>
              <div>PEC: caholding@pec.it</div>
            </Address>
            <p>Support:</p>
            <p>
              <a href="mailto:support@voihuworld.com"
                 className="underline underline-offset-4">support@voihuworld.com</a>
            </p>
          </Section>
        </div>
      </article>
    </main>
  );
}


/* ── layout helpers (shared style with /privacy) ────────────────────────── */

function Section({ n, title, children }) {
  return (
    <section className="mt-14 pt-10 border-t oh-hairline"
             data-testid={`terms-section-${n}`}>
      <div className="oh-label">Section {n}</div>
      <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-[1.15] mt-3">
        {title}
      </h2>
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

function List({ children }) {
  return (
    <ul className="list-disc pl-6 space-y-2 marker:text-[color:var(--oh-ink-3)]">
      {children}
    </ul>
  );
}

function Address({ children }) {
  return (
    <address className="not-italic leading-[1.75] my-2">{children}</address>
  );
}
